import { HeaderButton, Skeleton } from 'components/common';
import {
  AddIcon,
  ChevronDownIcon,
  CreateViewIcon,
  RevertIcon,
  TrashIcon,
  UpdateIcon,
} from 'components/common/Icons';
import { ViewModal } from 'components/core';
import { useContext, useState } from 'react';
import { ALL_FILTERS } from 'utils/constants/Filters';
import { ColumnContext, FilterContext, ViewContext } from 'utils/context';

import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Flex,
  Separator,
} from '@project44/manifest-ui-deprecated';
import { useTranslation } from '@project44/translations-utils';

type Variant = 'create' | 'delete' | 'revert' | 'update';

interface IViewButton {
  doSearch: () => void;
}

function ViewButton({ doSearch }: IViewButton) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [variant, setVariant] = useState<Variant | ''>('');
  const { hasFilters } = useContext(FilterContext);
  const {
    canUpdate,
    selectedView,
    viewType,
    savedOrders,
    savedShipments,
    getViewRequest,
    revertView,
  } = useContext(ViewContext);
  const { createView, deleteView, isLoading, updateView } =
    viewType === 'order' ? savedOrders : savedShipments;
  const { hasColumnsModified } = useContext(ColumnContext);
  const hasFiltersApplied = hasFilters(ALL_FILTERS);
  const isSavedView = !['all-shipments', 'all-orders'].includes(
    selectedView?.id || '',
  );

  return (
    <Flex>
      <ViewModal
        createView={createView}
        deleteView={deleteView}
        doSearch={doSearch}
        getViewRequest={getViewRequest}
        isLoading={isLoading}
        onClose={() => {
          setVariant('');
        }}
        revertView={revertView}
        selectedView={selectedView}
        updateView={updateView}
        variant={variant as Variant}
        viewType={viewType}
      />

      {isLoading && (
        <Skeleton
          variant="dark"
          sx={{ width: '160px', height: '40px', borderRadius: '2px 0 0 2px' }}
        />
      )}

      {!isLoading && !isSavedView && (
        <HeaderButton
          isDisabled={!hasFiltersApplied && !hasColumnsModified}
          startIcon={<AddIcon size="16px" />}
          sx={{
            whiteSpace: 'nowrap',
            width: '160px',
            justifyContent: 'center',
          }}
          onClick={() => {
            setVariant('create');
          }}
        >
          {t('core.createView')}
        </HeaderButton>
      )}

      {!isLoading && isSavedView && (
        <HeaderButton
          isDisabled={!hasFiltersApplied || !canUpdate}
          startIcon={<UpdateIcon size="16px" />}
          sx={{
            whiteSpace: 'nowrap',
            borderRadius: '2px 0 0 2px',
            width: '130px',
          }}
          onClick={() => {
            setVariant('update');
          }}
        >
          {t('core.saveView')}
        </HeaderButton>
      )}

      {!isLoading && isSavedView && (
        <Dropdown isOpen={isOpen} onChange={setIsOpen}>
          <DropdownTrigger data-testid="trigger" disabled={!isSavedView}>
            <HeaderButton
              sx={{ borderRadius: '0 2px 2px 0', paddingX: 1 }}
              hasModalActive={isOpen}
              onClick={() => {
                setIsOpen(true);
              }}
            >
              <ChevronDownIcon size="12px" />
            </HeaderButton>
          </DropdownTrigger>
          <DropdownMenu>
            <DropdownItem
              isDisabled={!hasFiltersApplied}
              startIcon={<CreateViewIcon />}
              onClick={() => {
                setVariant('create');
              }}
            >
              {t('core.createNewView')}
            </DropdownItem>
            <Separator />
            <DropdownItem
              isDisabled={!canUpdate}
              startIcon={<RevertIcon />}
              sx={{ color: 'status.danger.500' }}
              onClick={() => {
                setVariant('revert');
              }}
            >
              {t('core.revertView')}
            </DropdownItem>
            <DropdownItem
              startIcon={<TrashIcon />}
              sx={{ color: 'status.danger.500' }}
              onClick={() => {
                setVariant('delete');
              }}
            >
              {t('core.deleteView')}
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      )}
    </Flex>
  );
}

export default ViewButton;
